'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import HostSignupForm from './HostSignupForm';
import AppLogo from '@/components/ui/AppLogo';
import { Users, Shield, Star, Truck, MapPin, Package, CheckCircle, ArrowRight, Building2, Warehouse } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type AuthMode = 'login' | 'signup' | 'host-signup';

export default function AuthPageClient() {
  const [mode, setMode] = useState<AuthMode>('login');

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-between p-10 xl:p-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #0F172A 0%, #1A2540 50%, #0F172A 100%)' }}>

        {/* Animated background blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px]"
            style={{ background: mode === 'host-signup' ? 'radial-gradient(circle, rgba(5,150,105,0.25) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(26,86,219,0.25) 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full blur-[100px]"
            style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 70%)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[80px]"
            style={{ background: 'radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)' }} />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }} />

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <AppLogo size={24} />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">MuyuLink</span>
          </div>
          <p className="mt-2 text-slate-400 text-sm">Travelers helping travelers — across every route</p>
        </div>

        {/* Main content */}
        <div className="relative z-10 space-y-8">
          {mode === 'host-signup' ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(5,150,105,0.2)', border: '1px solid rgba(5,150,105,0.3)' }}>
                  <Warehouse size={16} className="text-emerald-400" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">Host Program</span>
              </div>
              <h2 className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-4">
                Earn from Your<br />Spare Space.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                  Become a<br />Storage Host.
                </span>
              </h2>
              <p className="text-slate-400 text-base leading-relaxed max-w-sm">
                List your spare room, shop, or warehouse as a secure storage space. Earn KES 60–500/hr from travelers who need to store luggage.
              </p>
              <div className="grid grid-cols-2 gap-3 mt-6">
                {[
                  { icon: Building2, text: 'Any space qualifies', color: 'text-emerald-400' },
                  { icon: Shield, text: 'Verified travelers only', color: 'text-blue-400' },
                  { icon: Star, text: 'Earn 5-star reviews', color: 'text-yellow-400' },
                  { icon: Package, text: 'You set your rates', color: 'text-orange-400' },
                ].map((stat) => (
                  <div key={stat.text}
                    className="flex items-center gap-2.5 rounded-xl px-3.5 py-3"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <stat.icon size={15} className={`${stat.color} shrink-0`} />
                    <span className="text-sm text-slate-300 font-medium leading-tight">{stat.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-4">
                Every Traveler<br />is a Courier.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-orange-400">
                  Every Route is<br />a Delivery.
                </span>
              </h2>
              <p className="text-slate-400 text-base leading-relaxed max-w-sm">
                Join Kenya's peer-to-peer travel marketplace — connect with verified travelers on matatus, buses, and trains.
              </p>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                {[
                  { icon: Truck, text: '2,400+ active travelers', color: 'text-blue-400' },
                  { icon: Package, text: '18,000+ packages delivered', color: 'text-orange-400' },
                  { icon: Shield, text: 'ID-verified travelers', color: 'text-emerald-400' },
                  { icon: Star, text: '4.8 avg. traveler rating', color: 'text-yellow-400' },
                ].map((stat) => (
                  <div key={stat.text}
                    className="flex items-center gap-2.5 rounded-xl px-3.5 py-3"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <stat.icon size={15} className={`${stat.color} shrink-0`} />
                    <span className="text-sm text-slate-300 font-medium leading-tight">{stat.text}</span>
                  </div>
                ))}
              </div>

              {/* Active route badge */}
              <div className="flex items-center gap-3 rounded-xl px-4 py-3.5 mt-4"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center shrink-0">
                  <MapPin size={15} className="text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Most active corridor today</p>
                  <p className="text-sm font-semibold text-white">Eldoret → Nairobi · 34 open orders</p>
                </div>
                <ArrowRight size={14} className="text-slate-500 ml-auto" />
              </div>
            </div>
          )}
        </div>

        {/* Bottom trust indicators */}
        <div className="relative z-10 flex items-center gap-6">
          {[
            { icon: CheckCircle, text: 'Free to join' },
            { icon: Shield, text: 'Secure payments' },
            { icon: Users, text: 'Verified community' },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-1.5 text-xs text-slate-500">
              <Icon size={12} className="text-slate-600" />
              {text}
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 bg-background overflow-y-auto min-h-screen lg:min-h-0">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
              <AppLogo size={22} />
            </div>
            <span className="text-xl font-bold text-foreground">MuyuLink</span>
          </div>

          {mode !== 'host-signup' && (
            <>
              {/* Heading */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground tracking-tight">
                  {mode === 'login' ? 'Welcome back' : 'Create your account'}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {mode === 'login' ? 'Sign in to continue to your MuyuLink dashboard' : 'Join thousands of Kenyans sending and delivering packages'}
                </p>
              </div>

              {/* Mode toggle */}
              <div className="tab-bar mb-6">
                <button onClick={() => setMode('login')} className={mode === 'login' ? 'tab-item-active' : 'tab-item'}>
                  Sign In
                </button>
                <button onClick={() => setMode('signup')} className={mode === 'signup' ? 'tab-item-active' : 'tab-item'}>
                  Create Account
                </button>
              </div>
            </>
          )}

          {mode === 'login' && <LoginForm onSwitchToSignup={() => setMode('signup')} />}
          {mode === 'signup' && <SignupForm onSwitchToLogin={() => setMode('login')} onHostSignup={() => setMode('host-signup')} />}
          {mode === 'host-signup' && <HostSignupForm onSwitchToLogin={() => setMode('login')} />}

          {/* Host signup CTA — shown on login mode only */}
          {mode === 'login' && (
            <div className="mt-6 p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(5,150,105,0.08) 0%, rgba(16,185,129,0.05) 100%)', border: '1px solid rgba(5,150,105,0.2)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(5,150,105,0.15)' }}>
                  <Warehouse size={17} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">Have a storage space?</p>
                  <p className="text-xs text-muted-foreground">Earn by hosting travelers&apos; luggage</p>
                </div>
              </div>
              <button
                onClick={() => setMode('host-signup')}
                className="w-full py-2.5 text-white rounded-xl text-sm font-bold hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', boxShadow: '0 4px 12px rgba(5,150,105,0.25)' }}
              >
                <Building2 size={15} />
                Apply to Become a Host
              </button>
            </div>
          )}

          {mode === 'host-signup' && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setMode('login')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to Sign In / Sign Up
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}